import { useEffect, useRef, useState } from 'react'
import { useReducedMotion } from '../../lib/use-reduced-motion'

interface NumberTickerProps {
  value: number
  /** Animation duration in ms. Defaults to 600. */
  durationMs?: number
  /** Decimal places to display. Defaults to 0. */
  decimals?: number
  /**
   * When true, every value change tweens from the previously displayed
   * number to the new one (not just the first mount). Useful for live
   * stat rails that update as the user changes form inputs.
   */
  animateOnChange?: boolean
  className?: string
}

/**
 * Counts up from 0 to `value` on first mount using a requestAnimationFrame
 * ease-out curve.
 *
 * - Subsequent `value` prop changes display the new number instantly.
 * - prefers-reduced-motion: displays final value immediately, no RAF loop.
 * - Renders inside a <span> with tabular-nums so width stays stable.
 */
export function NumberTicker({
  value,
  durationMs = 600,
  decimals = 0,
  animateOnChange = false,
  className,
}: NumberTickerProps): JSX.Element {
  const reduced = useReducedMotion()

  // Displayed value — starts at 0 for the first animated render, or at
  // the final value when reduced motion is requested.
  const [displayed, setDisplayed] = useState<number>(reduced ? value : 0)

  // True once the initial mount effect has fully run; prevents the value-
  // change effect from firing a spurious instant-update on first render.
  const initialRunDoneRef = useRef<boolean>(false)

  // ── Initial mount animation ───────────────────────────────────────────────
  useEffect(() => {
    initialRunDoneRef.current = true

    if (reduced) {
      setDisplayed(value)
      return
    }

    const start = performance.now()
    let rafId: number

    const tick = (now: number): void => {
      const elapsed = now - start
      const progress = Math.min(elapsed / durationMs, 1)
      // Ease-out cubic: decelerate into the final value.
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplayed(eased * value)
      if (progress < 1) {
        rafId = requestAnimationFrame(tick)
      }
    }

    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Subsequent value changes ──────────────────────────────────────────────
  // The `value` dep fires once on mount (initial render) and again whenever
  // `value` changes. We skip the initial fire because the mount effect above
  // already owns that path; we only want to react to *later* changes.
  //
  // animateOnChange=false (default): snap instantly to the new value.
  // animateOnChange=true: tween from the currently-displayed number to the
  // new target with the same ease-out curve as initial mount. Used by live
  // stat rails so the numbers feel alive when form inputs change.
  const isFirstValueEffectRef = useRef<boolean>(true)
  const displayedRef = useRef<number>(displayed)
  displayedRef.current = displayed

  useEffect(() => {
    if (isFirstValueEffectRef.current) {
      isFirstValueEffectRef.current = false
      return
    }
    if (!animateOnChange || reduced) {
      setDisplayed(value)
      return
    }
    const from = displayedRef.current
    const to = value
    if (from === to) return
    const start = performance.now()
    let rafId: number
    const tick = (now: number): void => {
      const elapsed = now - start
      const progress = Math.min(elapsed / durationMs, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplayed(from + (to - from) * eased)
      if (progress < 1) {
        rafId = requestAnimationFrame(tick)
      }
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [value, animateOnChange, reduced, durationMs])

  const formatted = displayed.toFixed(decimals)

  return (
    <span
      className={className}
      style={{ fontVariantNumeric: 'tabular-nums' }}
      aria-label={value.toFixed(decimals)}
    >
      {formatted}
    </span>
  )
}
